import clsx from 'clsx';
import { forwardRef, type ElementType, type ReactElement } from 'react';
import { isElement } from 'react-is';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type PolymorphicComponentProps,
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import * as styles from './NavItem.sty.js';

// #region Typings
/**
 * @public
 */
export interface NavItemOwnProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /**
   * The route where the NavItem navigates to.
   */
  href?: string;
  /**
   * Whether the NavItem is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * Whether the NavItem is selected.
   * @defaultValue false
   */
  isSelected?: boolean;
}

/**
 * @public
 */
export type NavItemProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  NavItemOwnProps
>;
// #endregion

/** @internal */
export function isNavItem(
  element: unknown,
): element is ReactElement<NavItemOwnProps> {
  return isElement(element) && element.type === NavItem;
}

/**
 * A navigation item which can be used to go to a specific route.
 * @public
 */
export const NavItem: <E extends ElementType = 'a'>(
  props: NavItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: NavItemProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      disabled = false,
      isSelected = false,
      as = 'a' as E,
      className: consumerClassName,
      style: consumerStyle = {},
      'data-testid': dataTestId,
      ...remainingProps
    }: NavItemProps<E> = props;

    return (
      <Button
        {...remainingProps}
        ref={forwardedRef}
        disabled={disabled}
        data-testid={dataTestId}
        as={as satisfies ElementType}
        className={clsx(
          styles.button,
          {
            [styles.isSelected]: isSelected,
            [styles.isDisabled]: disabled,
          },
          consumerClassName,
        )}
        style={{
          minWidth: 0,
          flexShrink: 0,
          display: 'flex',
          ...consumerStyle,
        }}
      />
    );
  },
);

(NavItem as typeof NavItem & { displayName: string }).displayName =
  'AppHeader.NavItem';
